import { create } from 'zustand';
import { getBalance, getTokenBalances } from './shyftService';
import { getTokenBalance } from './solanatrackerService';
import { Token } from '../types/supabase';
import { adminSupabase } from './supabaseService';
import { balanceDebugger } from '../utils/balanceUpdateDebugger';

interface TokenBalance {
    mint: string;
    balance: string;
    token?: Token | null;
    lastUpdated?: number; // Track when this token was last updated
}

interface WalletBalancesState {
    walletAddress: string | null;
    solBalance: number;
    tokenBalances: TokenBalance[];
    isLoading: boolean;
    error: string | null;
    lastUpdated: number | null;
    solLastUpdated: number | null; // Track when SOL balance was last updated

    // Actions
    setWallet: (address: string | null) => void;
    refreshBalances: (address?: string) => Promise<void>;
    refreshSolBalance: (address?: string) => Promise<number>;
    fetchTokenBalances: (address: string) => Promise<Record<string, string>>;
    getTokenInfo: (mint: string) => Promise<Token | null>;
}

export const useWalletBalances = create<WalletBalancesState>((set, get) => ({
    walletAddress: null,
    solBalance: 0,
    tokenBalances: [],
    isLoading: false,
    error: null,
    lastUpdated: null,
    solLastUpdated: null,

    setWallet: (address) => {
        const previousAddress = get().walletAddress;

        // If we have a previous address and we're clearing it, clean up state
        if (previousAddress && !address) {
            console.log(`Clearing wallet balance state for: ${previousAddress}`);

            // Reset all wallet-related state
            set({
                walletAddress: null,
                solBalance: 0,
                tokenBalances: [],
                lastUpdated: null,
                solLastUpdated: null,
                error: null
            });
            return;
        }

        // Normal wallet setting behavior
        set({ walletAddress: address });
        if (address) {
            get().refreshBalances(address);
        }
    },

    refreshBalances: async (address) => {
        const walletAddress = address || get().walletAddress;
        if (!walletAddress) return;

        set({ isLoading: true, error: null });

        try {
            // Get SOL balance
            const solBalance = await get().refreshSolBalance(walletAddress);

            // Get token balances
            const tokenBalancesMap = await get().fetchTokenBalances(walletAddress);

            // Convert to array format and filter out zero balances
            const tokenBalances: TokenBalance[] = [];

            // Process token balances and fetch token info
            await Promise.all(
                Object.entries(tokenBalancesMap).map(async ([mint, balance]) => {
                    // Skip tokens with zero balance
                    if (balance === '0') return;

                    // Fetch token info from the database
                    const tokenInfo = await get().getTokenInfo(mint);

                    // Create token balance entry
                    const tokenBalance: TokenBalance = {
                        mint,
                        balance,
                        token: tokenInfo,
                        lastUpdated: Date.now() // Set initial update time
                    };

                    tokenBalances.push(tokenBalance);
                })
            );

            set({
                solBalance,
                tokenBalances,
                isLoading: false,
                lastUpdated: Date.now(),
                solLastUpdated: Date.now(), // Set initial update time for SOL
                walletAddress
            });

        } catch (error) {
            console.error("Error refreshing wallet balances:", error);
            set({
                error: error instanceof Error ? error.message : "Failed to load balances",
                isLoading: false
            });
        }
    },

    refreshSolBalance: async (address) => {
        const walletAddress = address || get().walletAddress;
        if (!walletAddress) return 0;

        try {
            // Get SOL balance in lamports (1 SOL = 10^9 lamports)
            const lamports = await getBalance(walletAddress);

            // Convert to SOL and update state
            const solBalance = lamports / 1_000_000_000;

            // Update the SOL balance in state
            if (address === get().walletAddress || !address) {
                set({ solBalance, solLastUpdated: Date.now() });
            }

            return solBalance;
        } catch (error) {
            console.error("Error fetching SOL balance:", error);
            return 0;
        }
    },

    fetchTokenBalances: async (address: string): Promise<Record<string, string>> => {
        try {
            return await getTokenBalances(address);
        } catch (error) {
            console.error("Error fetching token balances:", error);
            return {};
        }
    },

    getTokenInfo: async (mint: string): Promise<Token | null> => {
        try {
            // Define the tables to check in order of priority
            const tables = ['new_tokens', 'graduating_tokens', 'graduated_tokens'];

            // Try to find the token in any of the tables
            for (const table of tables) {
                const { data, error } = await adminSupabase
                    .from(table)
                    .select('*')
                    .eq('mint', mint)
                    .maybeSingle();

                if (error) {
                    console.error(`Error fetching token info from ${table}:`, error);
                    continue; // Try the next table
                }

                if (data) {
                    return data; // Return the first matching token data
                }
            }

            // If we get here, the token wasn't found in any table
            return null;
        } catch (error) {
            console.error('Failed to fetch token info:', error);
            return null;
        }
    }
}));

// Function to handle token balance updates from gRPC
export const handleTokenBalanceUpdate = (
    walletAddress: string,
    mint: string,
    newBalance: string,
    forceRefresh: boolean = false
) => {
    const state = useWalletBalances.getState();

    // Only update if this is for the current wallet
    if (state.walletAddress !== walletAddress) {
        return;
    }

    // If forceRefresh is true, we should refresh this token's balance from the chain
    if (forceRefresh) {
        console.log(`[WalletBalance] Force refreshing balance for token ${mint} in wallet ${walletAddress}`);

        // Initiate an async refresh but don't wait for it
        (async () => {
            try {
                // Directly fetch latest balance from chain
                const latestBalance = await getTokenBalance(walletAddress, mint);
                console.log(`[WalletBalance] Force refreshed balance: ${latestBalance} for token ${mint}`);

                // Check if we have a real balance returned
                if (latestBalance !== null) {
                    // If real balance is returned, update with it
                    handleTokenBalanceUpdate(walletAddress, mint, latestBalance);
                } else if (newBalance !== null) {
                    // Otherwise use provided balance if we got one
                    handleTokenBalanceUpdate(walletAddress, mint, newBalance);
                }
            } catch (err) {
                console.error(`[WalletBalance] Error force refreshing balance for ${mint}:`, err);
            }
        })();

        // If we have a direct balance provided (like 0 after selling all tokens),
        // immediately update UI with it before refresh completes
        if (newBalance !== null) {
            // Continue with normal update using the provided balance
            console.log(`[WalletBalance] Using provided balance (${newBalance}) for immediate UI update`);
        } else {
            // If no balance provided with forceRefresh, just return and wait for the async refresh
            return;
        }
    }

    // Update the specific token balance
    const tokenBalances = [...state.tokenBalances];
    const existingIndex = tokenBalances.findIndex(tb => tb.mint === mint);
    const now = Date.now();

    if (existingIndex >= 0) {
        // Compare new balance with existing balance
        const currentBalance = tokenBalances[existingIndex].balance;
        const balanceChanged = currentBalance !== newBalance;

        // Get token info for debugging
        const tokenSymbol = tokenBalances[existingIndex].token?.symbol || null;

        // Log update for debugging
        balanceDebugger.logTokenUpdate(
            walletAddress,
            mint,
            tokenSymbol,
            currentBalance,
            newBalance
        );

        // Update existing token balance
        if (newBalance === '0') {
            // Remove zero balances
            tokenBalances.splice(existingIndex, 1);
            console.log(`Removed token ${mint} with zero balance`);
        } else if (balanceChanged) {
            // Only update if the balance has actually changed
            console.log(`Token ${mint} balance changed from ${currentBalance} to ${newBalance}`);
            tokenBalances[existingIndex] = {
                ...tokenBalances[existingIndex],
                balance: newBalance,
                lastUpdated: now // Update the lastUpdated timestamp
            };
        }
    } else if (newBalance !== '0') {
        // Add new token balance
        console.log(`Adding new token ${mint} with balance ${newBalance}`);
        balanceDebugger.logTokenUpdate(walletAddress, mint, null, '0', newBalance);

        tokenBalances.push({
            mint,
            balance: newBalance,
            token: null,
            lastUpdated: now // Set initial update time
        });

        // Fetch token info asynchronously
        state.getTokenInfo(mint).then(tokenInfo => {
            if (tokenInfo) {
                const updatedTokenBalances = useWalletBalances.getState().tokenBalances;
                const index = updatedTokenBalances.findIndex(tb => tb.mint === mint);
                if (index >= 0) {
                    updatedTokenBalances[index].token = tokenInfo;
                    useWalletBalances.setState({ tokenBalances: updatedTokenBalances });
                }
            }
        });
    }

    // Update state
    useWalletBalances.setState({
        tokenBalances,
        lastUpdated: now
    });
};

// Handle SOL balance update from gRPC
export const handleSolBalanceUpdate = (
    walletAddress: string,
    newBalance: number | null, // Can be null to force refresh
    forceRefresh: boolean = false
) => {
    const state = useWalletBalances.getState();

    // Only update if this is for the current wallet
    if (state.walletAddress !== walletAddress) {
        return;
    }

    // If forceRefresh flag is set or we got null balance, refresh from chain
    if (forceRefresh || newBalance === null) {
        console.log(`[WalletBalance] Force refreshing SOL balance for wallet ${walletAddress}`);

        // Initiate an async refresh but don't wait for it
        (async () => {
            try {
                // Get balance from chain
                const lamports = await getBalance(walletAddress);
                const freshBalance = lamports / 1_000_000_000;

                console.log(`[WalletBalance] Force refreshed SOL balance: ${freshBalance} for wallet ${walletAddress}`);

                // Use the fresh balance
                handleSolBalanceUpdate(walletAddress, freshBalance);
            } catch (err) {
                console.error(`[WalletBalance] Error force refreshing SOL balance:`, err);
            }
        })();

        // If we also got a direct balance update, continue with it
        // Otherwise just return and wait for the async refresh
        if (newBalance === null) {
            return;
        }
    }

    // Make sure we have a valid number
    if (isNaN(newBalance) || newBalance === null) {
        console.error(`Invalid SOL balance received for ${walletAddress}: ${newBalance}`);
        return;
    }

    // Check if balance has actually changed
    const currentBalance = state.solBalance;

    // For debug purposes
    console.log(`[SOL Debug] Main wallet ${walletAddress}:`);
    console.log(`- Current balance in state: ${currentBalance}`);
    console.log(`- New balance received: ${newBalance}`);
    console.log(`- New balance converted to SOL: ${newBalance}`);

    const balanceChanged = currentBalance !== newBalance;

    // Log update for debugging
    balanceDebugger.logSolUpdate(walletAddress, currentBalance, newBalance);

    if (balanceChanged) {
        console.log(`SOL balance changed from ${currentBalance} to ${newBalance} SOL for ${walletAddress}`);

        // Update SOL balance
        useWalletBalances.setState({
            solBalance: newBalance,
            lastUpdated: Date.now(),
            solLastUpdated: Date.now() // Update the solLastUpdated timestamp
        });
    }
};

// Function to handle wallet selection events and refresh balances
export const handleWalletSelectionEvent = (event: CustomEvent<{ walletPublicKey: string }>) => {
    const { walletPublicKey } = event.detail;

    if (!walletPublicKey) return;

    // Refresh the SOL balance for this wallet using Shyft RPC
    getBalance(walletPublicKey)
        .then(lamports => {
            const solBalance = lamports / 1_000_000_000;

            // Send an event with the refreshed balance
            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                    address: walletPublicKey,
                    balance: solBalance,
                    forceRefresh: false // This is a direct balance update, not a force refresh
                }
            }));
        })
        .catch(error => {
            console.error('[WalletBalance] Error refreshing SOL balance for wallet selection:', error);
            // Even if we fail, still dispatch a zero balance event so the UI can update
            // and show an error state instead of loading indefinitely
            window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                detail: {
                    address: walletPublicKey,
                    balance: 0,
                    error: true,
                    forceRefresh: false
                }
            }));
        });

    // Also refresh token balances
    getTokenBalances(walletPublicKey)
        .then(tokenBalances => {
            // Send events for each token balance
            Object.entries(tokenBalances).forEach(([mint, balance]) => {
                window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                    detail: {
                        address: walletPublicKey,
                        mint,
                        balance,
                        forceRefresh: false // This is a direct balance update, not a force refresh
                    }
                }));
            });
        })
        .catch(error => {
            console.error('[WalletBalance] Error refreshing token balances for wallet selection:', error);
            // Dispatch an empty token list event to indicate we're done trying
            window.dispatchEvent(new CustomEvent('ekko:token_balances_failed', {
                detail: {
                    address: walletPublicKey,
                    error: true
                }
            }));
        });
};

// Function to handle transfer completion events and refresh both source and destination wallets
export const handleTransferCompletionEvent = (event: CustomEvent<{
    fromWallet: string,
    toWallet: string,
    amount: number,
    tokenMint: string,
    transferType: string
}>) => {
    const { fromWallet, toWallet, transferType, tokenMint } = event.detail;

    console.log(`[WalletBalance] Transfer completed: ${transferType} from ${fromWallet?.slice(0, 8)} to ${toWallet?.slice(0, 8)}`);

    // Force refresh both wallets
    if (fromWallet) {
        console.log(`[WalletBalance] Force refreshing source wallet: ${fromWallet.slice(0, 8)}...`);
        handleWalletSelectionEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: fromWallet }
        }) as CustomEvent<{ walletPublicKey: string }>);
    }

    if (toWallet && toWallet !== fromWallet && !toWallet.includes('external')) {
        console.log(`[WalletBalance] Force refreshing destination wallet: ${toWallet.slice(0, 8)}...`);
        handleWalletSelectionEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: toWallet }
        }) as CustomEvent<{ walletPublicKey: string }>);
    }

    // Schedule additional refreshes
    setTimeout(() => {
        console.log(`[WalletBalance] Delayed transfer refresh (3s)...`);
        if (fromWallet) {
            handleWalletSelectionEvent(new CustomEvent('ekko:wallet_selected', {
                detail: { walletPublicKey: fromWallet }
            }) as CustomEvent<{ walletPublicKey: string }>);
        }
        if (toWallet && toWallet !== fromWallet && !toWallet.includes('external')) {
            handleWalletSelectionEvent(new CustomEvent('ekko:wallet_selected', {
                detail: { walletPublicKey: toWallet }
            }) as CustomEvent<{ walletPublicKey: string }>);
        }
    }, 3000);
};

// Function to handle balance cache invalidation
export const handleBalanceCacheInvalidation = (event: CustomEvent<{
    wallets: string[],
    timestamp: number
}>) => {
    const { wallets } = event.detail;

    console.log(`[WalletBalance] Invalidating balance cache for ${wallets.length} wallets`);

    // Clear any cached balance data for the affected wallets
    wallets.forEach(walletAddress => {
        if (walletAddress) {
            console.log(`[WalletBalance] Clearing cache for wallet: ${walletAddress.slice(0, 8)}...`);

            // Force immediate balance refresh for this wallet
            handleWalletSelectionEvent(new CustomEvent('ekko:wallet_selected', {
                detail: { walletPublicKey: walletAddress }
            }) as CustomEvent<{ walletPublicKey: string }>);
        }
    });
};

// Set up global event listener for wallet selection events
if (typeof window !== 'undefined') {
    window.addEventListener('ekko:wallet_selected', handleWalletSelectionEvent as EventListener);
    window.addEventListener('ekko:transfer_completed', handleTransferCompletionEvent as EventListener);
    window.addEventListener('ekko:invalidate_balance_cache', handleBalanceCacheInvalidation as EventListener);
}
